
'use client'
import { ALLOWED_PRICE_IMPACT_HIGH } from '@/constants'
import { ApprovalState } from '@/hooks/useApproveCallback'
import { WrapType } from '@/hooks/useWrapCallback'
import { ChainId, MON, NativeCurrency, Token, WMND } from '@monadex/sdk'
import { Field } from '@/state/swap/actions'
import { useSwapState } from '@/state/swap/hooks'
import { useState, useMemo } from 'react'
import { Box, CircularProgress } from '@mui/material'
import { useAppKit } from '@reown/appkit/react'
import { getRaffleTokenField } from './Swap'
interface Props {
  id: string
  account: string
  isSupportedNetwork: boolean
  currencies: {
    INPUT?: Token | NativeCurrency | undefined
    OUTPUT?: Token | NativeCurrency | undefined
  }
  formattedAmounts: {
    [x: string]: string
  }
  showWrap: boolean
  noRoute: boolean
  userHasSpecifiedInputOutput: boolean
  priceImpactSeverity: 0 | 4 | 3 | 1 | 2
  wrapInputError: string | undefined
  wrapType: WrapType
  chainId: ChainId
  swapInputError: string | undefined
  swapCallbackError: string | null
  showApproveFlow: boolean
  isValid: boolean
  approval: ApprovalState
  raffleApprovalState: ApprovalState
  inputApprovalState: ApprovalState
  handleApprove: () => Promise<void>
  onSwap: () => void
}

const SwapButton = (props: Props): JSX.Element => {
  const {
    id,
    account,
    isSupportedNetwork,
    currencies,
    formattedAmounts,
    showWrap,
    noRoute,
    showApproveFlow,
    userHasSpecifiedInputOutput,
    priceImpactSeverity,
    wrapInputError,
    wrapType,
    chainId,
    swapInputError,
    swapCallbackError,
    isValid,
    approval,
    raffleApprovalState,
    inputApprovalState,
    onSwap,
    handleApprove
  } = props

  function getWrapUnwrapDetails (wrapType: WrapType, chainId: number, onSwap: () => void): {
    text: JSX.Element
    action: () => void
    disabled: boolean
  } {
    let wrapText: JSX.Element
    let symbol: string | undefined
    let disabled: boolean

    switch (wrapType) {
      case WrapType.WRAP:
        wrapText = <>Wrap</>
        symbol = MON.symbol
        disabled = false
        break
      case WrapType.UNWRAP:
        wrapText = <>Unwrap</>
        symbol = WMND[10143].symbol
        disabled = false
        break
      case WrapType.WRAPPING:
        wrapText = (
              <>
                <CircularProgress size={16} className='ml-2' color='secondary' /> Wrapping
              </>
        )
        symbol = MON.symbol
        disabled = true
        break
      case WrapType.UNWRAPPING:
        wrapText = (
              <>
                <CircularProgress size={16} className='ml-2' color='secondary' /> Unwrapping
              </>
        )
        symbol = WMND[10143].symbol
        disabled = true
        break
      default:
        wrapText = <></>
        symbol = '[INVALID SYMBOL]'
        disabled = true
    }

    return {
      text: (
            <>
              {wrapText} {symbol ?? '[INVALID SYMBOL]'}
            </>
      ),
      action: onSwap,
      disabled
    }
  }

  const [, setApprovalSubmitted] = useState<boolean>(false)
  const { open } = useAppKit()
  const { enterRaffle } = useSwapState()

  const buttonState = useMemo(() => {
    if (!account) return { text: 'Connect Wallet', action: open, disabled: false }
    if (account && !isSupportedNetwork) return { text: 'Switch Network', action: () => open, disabled: false }
    if ((currencies[Field.INPUT] == null) || (currencies[Field.OUTPUT] == null)) return { text: 'Select a token', action: () => {}, disabled: true }
    if (formattedAmounts[Field.INPUT] === '' && formattedAmounts[Field.OUTPUT] === '') return { text: 'Enter Amount', action: () => {}, disabled: true }

    if (showWrap) {
      return getWrapUnwrapDetails(wrapType, chainId, onSwap)
    }
    if (noRoute && userHasSpecifiedInputOutput) {
      return { text: 'Insufficient liquidity for this trade', action: () => {}, disabled: true }
    }
    if (priceImpactSeverity > 3) {
      return {
        text: `Price impact is more than ${Number(ALLOWED_PRICE_IMPACT_HIGH.multiply('100').toFixed(4))}`,
        action: () => {},
        disabled: true
      }
    }

    // For raffle flow
    // first we get where the whitelisted token is located
    const raffleField = getRaffleTokenField(currencies) as Field
    // approval flow is taking in count raffles and no raffles, when raffles is not enabled approval will only take in count the input token, when raffles in enables
    // approval will take in count the approval of input and output token
    switch (true) {
      // When input token approval is pending
      case inputApprovalState === ApprovalState.PENDING:
        return { text: 'Approving Input Token', action: () => {}, disabled: true }

        // When raffle token approval is pending
      case raffleApprovalState === ApprovalState.PENDING:
        return { text: 'Approving Raffle Token', action: () => {}, disabled: true }

        // Handle cases where approval is needed
      case inputApprovalState === ApprovalState.NOT_APPROVED || raffleApprovalState === ApprovalState.NOT_APPROVED:
        // Raffle is enabled
        if (enterRaffle) {
          // Input token approval is needed first
          if (inputApprovalState === ApprovalState.NOT_APPROVED) {
            return {
              text: `Approve ${currencies[Field.INPUT]?.symbol ?? '[INVALID SYMBOL]'}`,
              action: handleApprove,
              disabled: false
            }
          }
          // After input token is approved, raffle token approval may be needed
          else if (raffleApprovalState === ApprovalState.NOT_APPROVED) {
            return {
              text: `Approve ${currencies[raffleField]?.symbol ?? '[INVALID SYMBOL]'}`,
              action: handleApprove,
              disabled: false
            }
          }
        }
        // Raffle is not enabled, only need input token approval
        else if (inputApprovalState === ApprovalState.NOT_APPROVED) {
          return {
            text: `Approve ${currencies[Field.INPUT]?.symbol ?? '[INVALID SYMBOL]'}`,
            action: handleApprove,
            disabled: false
          }
        }

        // All necessary approvals are done
      case inputApprovalState === ApprovalState.APPROVED &&
               (!enterRaffle || raffleApprovalState === ApprovalState.APPROVED):
        return {
          text: swapInputError ?? swapCallbackError ?? 'Swap',
          action: onSwap,
          disabled: !isValid || !!swapCallbackError
        }

        // Default loading state
      default:
        return {
          text: <>Loading <CircularProgress size={16} className='ml-2' color='secondary' /></>,
          action: () => {},
          disabled: true
        }
    }
  }, [
    account,
    isSupportedNetwork,
    currencies,
    formattedAmounts,
    showWrap,
    noRoute,
    userHasSpecifiedInputOutput,
    priceImpactSeverity,
    wrapInputError,
    wrapType,
    chainId,
    swapInputError,
    swapCallbackError,
    approval,
    isValid,
    onSwap,
    handleApprove
  ])

  const handleClick = async (): Promise<void> => {
    await buttonState.action()
  }

  return (

        <button

          id={id}
          className='w-full bg-primary py-4 px-4 rounded-md disabled:opacity-40 bg-opacity-90 text-lg mt-5'
          disabled={buttonState.disabled}
          onClick={handleClick}
        >
          {approval === ApprovalState.PENDING
            ? (
              <Box className='flex items-center justify-center'>
                Approving <CircularProgress size={16} className='ml-2' />
              </Box>
              )
            : (
                buttonState.text
              )}
        </button>
  )
}

export default SwapButton
